/* eslint-disable max-lines-per-function */
import dayjs from '@/domain/mitarbeiter/shared/utils/dayjs';
import { toast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { useCreateAndUpdateSchema } from '@/domain/mitarbeiter/features/rotationsplan/features/shared/formSchema/useCreateAndUpdateSchema';
import { useMitarbeiterAppForm } from '@/domain/mitarbeiter/shared/components/form/hooks';
import z from 'zod';
import { Rotationsplan } from '@/domain/mitarbeiter/features/rotationsplan/entities/Rotationsplan';
import { useUpdateArbeitsbereichStation } from '@/domain/mitarbeiter/features/rotationsplan/hooks/useUpdateArbeitsbereichStation/useUpdateArbeitsbereichStation';
import { ArbeitsbereichOption } from '@/domain/mitarbeiter/features/rotationsplan/entities/ArbeitsbereichOption';
import { ArbeitsbereichStation } from '@/domain/mitarbeiter/features/rotationsplan/entities/Station';
import {
  getErinnerungsZeitFromFristDatum,
  getErinnerungsZeitFromFristDatumAndErinnerungsDatum,
} from '@/domain/mitarbeiter/shared/utils/getErinerungsZeitFromFristDatum/getErinnerungsZeitFromFristDatum';
import { ApolloClient } from '@apollo/client';
import { Rotationsplan_UpdateArbeitsbereichStationMutation } from '@/gql/graphql';

export function useForm({
  rotationsplan,
  abschnittId,
  station,
}: {
  rotationsplan: Rotationsplan;
  abschnittId: string;
  station: ArbeitsbereichStation;
}) {
  const [updateArbeitsbereichStation] = useUpdateArbeitsbereichStation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const schema = useCreateAndUpdateSchema();

  function getToastMessage(
    result: ApolloClient.MutateResult<
      Rotationsplan_UpdateArbeitsbereichStationMutation,
      undefined
    >,
  ) {
    const success =
      result.data?.mitarbeiter_updateArbeitsbereichStation.success;

    let message = t(
      'domain.mitarbeiter.rotationsplan.rotationsplaene.updateStation.success',
    );

    if (
      (success?.fristResult?.__typename ===
        'mitarbeiter_CreateOrUpdateArbeitsbereichStationFristResponse' &&
        success?.fristResult?.error) ||
      (success?.fristResult?.__typename ===
        'mitarbeiter_DeleteArbeitsbereichStationFristResponse' &&
        success?.fristResult?.error)
    ) {
      message +=
        ' ' +
        t(
          'domain.mitarbeiter.rotationsplan.rotationsplaene.updateStation.fristUpdateError',
        );
    }

    if (
      (success?.fristResult?.__typename ===
        'mitarbeiter_CreateOrUpdateArbeitsbereichStationFristResponse' &&
        success?.fristResult?.success) ||
      (success?.fristResult?.__typename ===
        'mitarbeiter_DeleteArbeitsbereichStationFristResponse' &&
        success?.fristResult?.fristId)
    ) {
      message +=
        ' ' +
        t(
          'domain.mitarbeiter.rotationsplan.rotationsplaene.updateStation.fristUpdateSuccess',
        );
    }
    return message;
  }

  async function handleSubmit({
    value,
    abschnittId,
    stationId,
    rotationsplanId,
  }: {
    value: z.infer<typeof schema>;
    abschnittId: string;
    stationId: string;
    rotationsplanId: string;
  }) {
    try {
      const result = await updateArbeitsbereichStation({
        input: {
          stationId: stationId,
          abschnittId,
          arbeitsbereichId: value.arbeitsbereich.id,
          lernziele: value.lernziele ?? '',
          von: dayjs(value.von).format('YYYY-MM-DD'),
          bis: dayjs(value.bis).format('YYYY-MM-DD'),
          rotationsplanId: rotationsplanId,
          abteilungsleiterId: value.abteilungsleiter?.id ?? undefined,
          frist: value.fristErinnerung
            ? {
                erinnerungAn: value.erinnerungAn,
                erinnerungsZeit: value.erinnerungsZeit.map((zeit) =>
                  getErinnerungsZeitFromFristDatum(
                    zeit,
                    dayjs(value.von).format('YYYY-MM-DD'),
                  ),
                ),
              }
            : undefined,
          standortwechselId: station.standortwechselId,
        },
      });

      const success =
        result.data?.mitarbeiter_updateArbeitsbereichStation.success;

      if (success?.station) {
        const message = getToastMessage(result);
        navigate(`/mitarbeiter/rotationsplan/${rotationsplan.id}`);

        toast({
          title: message,
        });
        return;
      }

      toast({
        title: t(
          'domain.mitarbeiter.rotationsplan.rotationsplaene.updateStation.error',
        ),
      });
    } catch (error) {
      Sentry.captureException(error);
      toast({
        title: t(
          'domain.mitarbeiter.rotationsplan.rotationsplaene.updateStation.error',
        ),
      });
    }
  }

  const form = useMitarbeiterAppForm({
    defaultValues: {
      standort: station.arbeitsbereich.standort,
      von: station.von,
      bis: station.bis,
      lernziele: station.lernziele,
      arbeitsbereich: new ArbeitsbereichOption({
        id: station.arbeitsbereich.id,
        markierung: station.arbeitsbereich.markierung,
        name: station.arbeitsbereich.name,
      }),
      abteilungsleiter: station.abteilungsleiter,
      fristErinnerung: station.frist ? true : false,
      erinnerungsZeit: station.frist
        ? station.frist.erinnerungenAm.map((e) =>
            getErinnerungsZeitFromFristDatumAndErinnerungsDatum(
              e.datum,
              dayjs(station.von).format('YYYY-MM-DD'),
            ),
          )
        : [],
      erinnerungAn: station.frist
        ? station.frist.erinnerungenAm.map((e) => e.managerId)
        : station.abteilungsleiter
          ? [station.abteilungsleiter.id]
          : [],
    } satisfies z.infer<typeof schema> as z.infer<typeof schema>,
    validators: {
      onSubmit: schema,
    },
    onSubmit: async ({ value }) => {
      await handleSubmit({
        abschnittId,
        rotationsplanId: rotationsplan.id,
        stationId: station.id,
        value,
      });
    },
  });

  return form;
}
